import { BudgetSpec } from "../types/categories.type";
import { Product } from "../types/product.type";

/**
 * Budget enforcement — a PURE, AI-free module run at page creation AFTER real
 * retailer prices are fetched (create-category-page-core.ts), and unit-testable
 * in isolation.
 *
 * WHY THIS EXISTS
 * The generation prompt asks the model to respect a stated ceiling, but the
 * model prices from memory; the truth arrives later with the Amazon/link fetch.
 * This classifier compares each product's FETCHED numeric price against the
 * shopper's ceiling and decides what ships: within-budget picks stay, exactly
 * ONE over-budget pick may stay as the deliberate "stretch" (honestly annotated
 * — see stretch-reason.ts), the rest are dropped.
 *
 * CONTRACT
 * - Compare ONLY `price.listingPrice ?? price.lowestPrice` (retailer-fetched),
 *   times `budget.quantity` when the number covers several units (a set of
 *   four tires is four tire prices). NEVER the AI-text `product.priceRange` —
 *   a known-unreliable field.
 * - The stretch pick is the LOWEST-INDEX over-budget product: the generation
 *   order encodes the model's conviction, so the highest-ranked over-budget
 *   pick is the one it believed in most. No numeric cap (owner decision) —
 *   the generation prompt raises the bar with distance; enforcement trusts
 *   the ONE slot and drops the rest.
 * - NEVER thin a page below quality: if fewer than MIN_WITHIN_TO_DROP priced
 *   in-budget products remain, drop nothing (degraded mode) and only annotate.
 * - Products with no fetched price are kept, never stretch-eligible, and never
 *   counted as "within" for the drop gate.
 */

/** Never drop below this many priced in-budget products. */
export const MIN_WITHIN_TO_DROP = 3;

export type BudgetClass = "within" | "over" | "unknown";

export interface BudgetEnforcementResult {
  /** False when enforcement was skipped (no budget / no priced products). */
  enforced: boolean;
  /** Parallel to the input products array. */
  classes: BudgetClass[];
  /** Index of THE stretch pick to annotate, or null. */
  stretchIndex: number | null;
  /** Indices to remove from the page, in ascending order. */
  dropIndices: number[];
  /**
   * ORIGINAL indices of the kept products in budget-first display order:
   * within-budget picks (AI-conviction order preserved) → unpriced picks →
   * the stretch pick DEAD LAST, so a budget page opens on its promise and
   * closes on the one "worth going over?" moment beside its concession note.
   * Drops are already excluded — applying this permutation applies the drops.
   * Callers that must pin position 0 (product-type pages) ignore this and
   * filter by dropIndices instead.
   */
  keptDisplayOrder: number[];
  stats: { within: number; over: number; unknown: number };
  /** Set when over-budget picks were kept because dropping would gut the page. */
  degraded?: "insufficient-within-budget";
}

/** within (original order) → unknown (original order) → stretch last. */
function buildKeptDisplayOrder(
  classes: BudgetClass[],
  stretchIndex: number | null,
  dropIndices: number[],
): number[] {
  const dropped = new Set(dropIndices);
  const within: number[] = [];
  const unknown: number[] = [];
  const trailing: number[] = [];
  classes.forEach((cls, index) => {
    if (dropped.has(index)) return;
    if (index === stretchIndex) return; // appended last
    if (cls === "within") within.push(index);
    else if (cls === "unknown") unknown.push(index);
    // kept "over" that is not the stretch (degraded mode / never-drop pins):
    // group with the stretch at the tail so the budget promise leads.
    else trailing.push(index);
  });
  const order = [...within, ...unknown, ...trailing];
  if (stretchIndex !== null && !dropped.has(stretchIndex)) order.push(stretchIndex);
  return order;
}

/** The retailer-fetched numeric price, or null. Never reads AI-text priceRange. */
export function effectiveNumericPrice(product: Pick<Product, "price">): number | null {
  const price = product.price?.listingPrice ?? product.price?.lowestPrice;
  return typeof price === "number" && Number.isFinite(price) && price > 0 ? price : null;
}

/** How many units the shopper's number covers: a set of four tires is 4. */
export function budgetUnits(budget: Pick<BudgetSpec, "quantity"> | null | undefined): number {
  const quantity = budget?.quantity;
  return typeof quantity === "number" && Number.isFinite(quantity) && quantity > 1
    ? Math.floor(quantity)
    : 1;
}

/**
 * The price to hold against the ceiling: the fetched unit price times the
 * units the number covers, rounded to the cent so 4 × 299.99 is 1199.96 and
 * never a float a hair over it. Null when no price was fetched.
 */
export function budgetComparablePrice(
  product: Pick<Product, "price">,
  budget: Pick<BudgetSpec, "quantity"> | null | undefined,
): number | null {
  const price = effectiveNumericPrice(product);
  return price === null ? null : Math.round(price * budgetUnits(budget) * 100) / 100;
}

export function classifyAgainstBudget(
  products: Array<Pick<Product, "price">>,
  budget: BudgetSpec,
  opts?: {
    /** Indices that must never be dropped (e.g. [0] on product-type pages — the searched product IS the page). */
    neverDropIndices?: number[];
    /** Override for budget.stretch; default allows the one stretch pick. */
    allowStretch?: boolean;
  },
): BudgetEnforcementResult {
  const neverDrop = new Set(opts?.neverDropIndices ?? []);
  const allowStretch = opts?.allowStretch ?? budget.stretch !== false;

  const classes: BudgetClass[] = products.map((product) => {
    const price = budgetComparablePrice(product, budget);
    if (price === null) return "unknown";
    return price <= budget.max ? "within" : "over";
  });

  const stats = {
    within: classes.filter((c) => c === "within").length,
    over: classes.filter((c) => c === "over").length,
    unknown: classes.filter((c) => c === "unknown").length,
  };

  if (stats.over === 0) {
    // Nothing over budget — the generation respected the ceiling outright.
    // Display order still groups unpriced picks after the priced ones.
    return {
      enforced: true,
      classes,
      stretchIndex: null,
      dropIndices: [],
      keptDisplayOrder: buildKeptDisplayOrder(classes, null, []),
      stats,
    };
  }

  const overIndices = classes
    .map((c, i) => (c === "over" ? i : -1))
    .filter((i) => i !== -1);

  if (stats.within < MIN_WITHIN_TO_DROP) {
    // Degraded: generation largely missed the ceiling. Dropping would gut the
    // page, so keep everything and annotate the over-budget pick CLOSEST to
    // the budget as the stretch (the most honest "slightly over" candidate).
    let cheapestOver: number | null = null;
    for (const i of overIndices) {
      const price = effectiveNumericPrice(products[i])!;
      if (cheapestOver === null || price < effectiveNumericPrice(products[cheapestOver])!) {
        cheapestOver = i;
      }
    }
    const degradedStretch = allowStretch ? cheapestOver : null;
    return {
      enforced: true,
      classes,
      stretchIndex: degradedStretch,
      dropIndices: [],
      keptDisplayOrder: buildKeptDisplayOrder(classes, degradedStretch, []),
      stats,
      degraded: "insufficient-within-budget",
    };
  }

  // Normal mode: ONE stretch slot (highest-ranked over-budget pick), the rest
  // dropped — except never-drop indices, which are kept (and only annotated
  // when they ARE the chosen stretch).
  const stretchIndex = allowStretch ? (overIndices[0] ?? null) : null;
  const dropIndices = overIndices.filter((i) => i !== stretchIndex && !neverDrop.has(i));

  return {
    enforced: true,
    classes,
    stretchIndex,
    dropIndices,
    keptDisplayOrder: buildKeptDisplayOrder(classes, stretchIndex, dropIndices),
    stats,
  };
}
